import React, { useMemo, useState } from 'react';
import { BarChart3, Calendar, ChevronDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
const TIMEFRAMES = ['Today', 'Yesterday', '7D', '30D', 'MTD'] as const;
const LIST_FILTERS = ['All Lists', 'Dominate', 'VA Cash Out', 'Rate Watch'] as const;
type Timeframe = (typeof TIMEFRAMES)[number];
type ListFilter = (typeof LIST_FILTERS)[number];
// Deterministic pseudo-random for stable charts per (timeframe, list)
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}
function buildSeries(timeframe: Timeframe, list: ListFilter) {
  const seed = timeframe.split('').reduce((a, c) => a + c.charCodeAt(0), 0) * 31 + list.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const rand = seededRandom(seed);
  let labels: string[] = [];
  if (timeframe === 'Today' || timeframe === 'Yesterday') {
    labels = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
  } else if (timeframe === '7D') {
    labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  } else if (timeframe === '30D') {
    labels = Array.from({
      length: 15
    }, (_, i) => `D${i * 2 + 1}`);
  } else {
    labels = Array.from({
      length: 12
    }, (_, i) => `W${i + 1}`);
  }
  const scale = list === 'All Lists' ? 1 : 0.4;
  return labels.map(time => ({
    time,
    entered: Math.floor((1500 + rand() * 4500) * scale),
    exited: Math.floor((1200 + rand() * 4000) * scale)
  }));
}
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-slate-900 border border-slate-800 p-3 rounded-lg shadow-xl" data-id="element-27">
        <p className="text-slate-400 text-xs mb-2 font-medium" data-id="element-28">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4 mb-1 last:mb-0" data-id="element-29">
            <div className="flex items-center gap-2" data-id="element-30">
              <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: entry.color
          }} data-id="element-31"></div>
              <span className="text-sm text-slate-300" data-id="element-32">{entry.name}</span>
            </div>
            <span className="text-sm font-bold text-white tabular-nums" data-id="element-33">
              {entry.value.toLocaleString()}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function AnalyticsSection() {
  const [timeframe, setTimeframe] = useState<Timeframe>('Today');
  const [listFilter, setListFilter] = useState<ListFilter>('All Lists');
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const data = useMemo(() => buildSeries(timeframe, listFilter), [timeframe, listFilter]);
  return <div className="bg-slate-900/80 border border-slate-800/60 rounded-xl p-5 mb-8" data-id="element-34">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6" data-id="element-35">
        <div className="flex items-center gap-2" data-id="element-36">
          <div className="p-2 bg-indigo-500/10 rounded-lg" data-id="element-37">
            <BarChart3 className="w-5 h-5 text-indigo-400" data-id="element-38" />
          </div>
          <div data-id="element-39">
            <h2 className="text-lg font-semibold text-white" data-id="element-40">
              Historical Movement Analytics
            </h2>
            <p className="text-sm text-slate-400" data-id="element-41">
              Leads entering and exiting routing lists over time
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap" data-id="element-42">
          <div className="flex bg-slate-950 border border-slate-800 rounded-lg p-1" data-id="element-43">
            {LIST_FILTERS.map(list => <button key={list} onClick={() => setListFilter(list)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${listFilter === list ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-44">
                {list}
              </button>)}
          </div>

          <div className="relative" data-id="element-45">
            <button onClick={() => setTimeframeOpen(v => !v)} onBlur={() => setTimeout(() => setTimeframeOpen(false), 150)} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 transition-colors" data-id="element-46">
              <Calendar className="w-4 h-4 text-slate-400" data-id="element-47" />
              {timeframe}
              <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${timeframeOpen ? 'rotate-180' : ''}`} data-id="element-48" />
            </button>
            {timeframeOpen && <div className="absolute right-0 top-full mt-1 w-32 bg-slate-900 border border-slate-800 rounded-lg shadow-xl z-20 py-1" data-id="element-49">
                {TIMEFRAMES.map(tf => <button key={tf} onMouseDown={e => {
              e.preventDefault();
              setTimeframe(tf);
              setTimeframeOpen(false);
            }} className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${timeframe === tf ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60'}`} data-id="element-50">
                    {tf}
                  </button>)}
              </div>}
          </div>
        </div>
      </div>

      <div className="h-[300px] w-full" data-id="element-51">
        <ResponsiveContainer width="100%" height="100%" data-id="element-52">
          <AreaChart data={data} margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 0
        }} data-id="element-53">
            <defs data-id="element-54">
              <linearGradient id="colorEntered" x1="0" y1="0" x2="0" y2="1" data-id="element-55">
                <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} data-id="element-56" />
                <stop offset="95%" stopColor="#34d399" stopOpacity={0} data-id="element-57" />
              </linearGradient>
              <linearGradient id="colorExited" x1="0" y1="0" x2="0" y2="1" data-id="element-58">
                <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.3} data-id="element-59" />
                <stop offset="95%" stopColor="#fbbf24" stopOpacity={0} data-id="element-60" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-61" />
            <XAxis dataKey="time" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} dy={10} data-id="element-62" />
            <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value / 1000}k`} data-id="element-63" />
            <Tooltip content={<CustomTooltip data-id="element-65" />} cursor={{
            stroke: '#334155',
            strokeWidth: 1
          }} data-id="element-64" />
            <Legend iconType="circle" wrapperStyle={{
            fontSize: '12px',
            paddingTop: '20px'
          }} data-id="element-66" />
            <Area type="monotone" dataKey="entered" name="Leads Entered" stroke="#34d399" strokeWidth={2} fillOpacity={1} fill="url(#colorEntered)" data-id="element-67" />
            <Area type="monotone" dataKey="exited" name="Leads Exited" stroke="#fbbf24" strokeWidth={2} fillOpacity={1} fill="url(#colorExited)" data-id="element-68" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
}